// dependencies
import PropTypes from "prop-types";
import React, { useLayoutEffect } from "react";

import { faSort } from "@fortawesome/pro-duotone-svg-icons";
import { faSort as faSortSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import get from "../../../utils/GetNested";

import { Grid, GridCol } from "../../Grid";
import Text from "../../Text";
import css from "../Table.module.scss";

const TableHead = (props) => {
	const {
		columns,
		subheaders = {},
		colWidths,
		activeSort = null,
		onColumnClick = () => {},
		highlightColumn = null,
		onUpdateHighlight = () => {},
		defaultCellClassName = "",
		activeSortDirection = null,
		currentScreenWidth,
		tableHeadRef = null,
		clientHeight = null,
		tableColumnRefs = null,
		dividedColumns = false,
	} = props;

	const handleColumnClick = (column) => {
		// gets the next direction value, toggles to the opposite direction
		// if the user clicks the active filter
		const nextDirection =
			(activeSortDirection === "desc" || activeSortDirection === "descending") && activeSort === column
				? "asc"
				: "desc";
		// update parent handler
		onColumnClick(column, nextDirection);
	};

	useLayoutEffect(() => {
		// sets a timeout (0ms) to wait for the ref to be connected to the DOM
		setTimeout(() => {
			onUpdateHighlight();
		}, 0);
	}, [highlightColumn]);

	return (
		<div
			ref={tableHeadRef}
			className={`${css.tableHead} ${dividedColumns && css.dividedColumns}`}
			style={{ gridTemplateColumns: colWidths, height: clientHeight }}
		>
			{columns.map((col, index) => {
				const {
					id,
					sortName = id,
					isSortable = true,
					headCellClass = null,
					renderHeadCell = null,
					centerHeader = false,
					mobileViewHideColumn,
					isSticky = false,
				} = col;

				const hasActiveSort = activeSort === sortName;
				const cellClassName = headCellClass || defaultCellClassName;
				const isAscending = (activeSortDirection ?? "").toLowerCase().startsWith("asc");

				if (renderHeadCell) {
					return (
						<div
							ref={(el) => (tableColumnRefs.current[index] = el)}
							key={id}
							className={` ${css.tableHeadCell} ${css.column} ${dividedColumns ? css.dividedColumns : ""} ${index === columns.length - 1 ? css.lastColumn : ""} ${hasActiveSort ? css.hasFilter : ""} ${isSortable ? css.isSortable : ""} ${cellClassName}`}
						>
							{renderHeadCell()}
						</div>
					);
				}
				return (
					<div id={id} key={id} className={mobileViewHideColumn > currentScreenWidth ? css.hiddenColumn : ""}>
						<div
							ref={(el) => (tableColumnRefs.current[index] = el)}
							className={` ${css.tableHeadCell} ${css.column} ${hasActiveSort ? css.hasFilter : ""} ${isSticky ? css.stickyColumn : ""} ${isSortable ? css.isSortable : ""} ${cellClassName} ${dividedColumns ? css.dividedColumns : ""} ${index === columns.length - 1 ? css.lastColumn : ""} `}
							style={subheaders[id] || centerHeader ? { textAlign: "center" } : {}}
							onClick={() => (isSortable ? handleColumnClick(sortName) : null)}
						>
							<span
								data-testid={`${isSortable ? "sortable-" : ""}${hasActiveSort ? "active-" : ""}header`}
								className={css.span}
							>
								<Grid cols={2} gap={1}>
									<GridCol width="1fr">
										<Text size="paragraph-3" weight="semi-bold" className={css.label}>
											{get(col, "label", " ")}
										</Text>
									</GridCol>
									<GridCol width="16px">
										{isSortable ? (
											<div
												data-testid={hasActiveSort ? "active-icon" : "inactive-icon"}
												className={`${css.sortIconWrapper} ${
													hasActiveSort ? css.isActiveSort : ""
												}`}
											>
												<div className={`${css.sortIcon}`}>
													{hasActiveSort ? (
														<FontAwesomeIcon
															rotation={isAscending ? 0 : 180}
															icon={faSort}
														/>
													) : (
														<FontAwesomeIcon icon={faSortSolid} />
													)}
												</div>
											</div>
										) : null}
									</GridCol>
								</Grid>
							</span>
						</div>
						{subheaders[id] ? (
							<div className={css.subheaderContainer}>
								{subheaders[id].map((subheader) => {
									const subID = subheader.id;
									const subSortName = subheader.sortName || subID;
									const subIsSortable = subheader.isSortable || true;
									const subHasFilter = activeSort === subSortName;
									return (
										<div
											key={subID}
											className={`${css.subheaderItem} ${subHasFilter ? css.hasFilter : ""} ${
												subIsSortable ? css.subIsSortable : ""
											}`}
										>
											<span
												onClick={() => (subIsSortable ? handleColumnClick(subSortName) : null)}
												className={css.span}
											>
												<div className={`${css.label} ${css.subheaderItem}`}>
													{get(subheader, "label", " ")}
												</div>

												{subHasFilter && subIsSortable ? (
													<div className={css.sortIconWrapper}>
														<div
															className={`${css.sortIcon} ${
																activeSortDirection === "asc" ||
																activeSortDirection === "ascending"
																	? css.isActive
																	: ""
															}`}
														>
															<FontAwesomeIcon rotation={180} icon={faSort} />
														</div>
														<div
															className={`${css.sortIcon} ${
																activeSortDirection === "dsc" ||
																activeSortDirection === "descending"
																	? css.isActive
																	: ""
															}`}
														>
															<FontAwesomeIcon icon={faSort} />
														</div>
													</div>
												) : null}
											</span>
										</div>
									);
								})}
							</div>
						) : null}
					</div>
				);
			})}
		</div>
	);
};

TableHead.propTypes = {
	onColumnClick: PropTypes.func,
	onUpdateHighlight: PropTypes.func,
	activeSort: PropTypes.string,
	activeSortDirection: PropTypes.string,
	highlightColumn: PropTypes.string,
	defaultCellClassName: PropTypes.string,
	subheaders: PropTypes.object,
	columns: PropTypes.array.isRequired,
	colWidths: PropTypes.string.isRequired,
	currentScreenWidth: PropTypes.number.isRequired,
	tableHeadRef: PropTypes.object,
	clientHeight: PropTypes.number,
	tableColumnRefs: PropTypes.object,
	dividedColumns: PropTypes.bool,
};

export default TableHead;
